import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gatepassAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiKey, FiClock, FiCheckCircle, FiAlertCircle, FiBell, FiCopy } from 'react-icons/fi';
import './GatePasses.css';

const GatePasses = () => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPasses();
  }, []);

  const fetchPasses = async () => {
    try {
      const response = await gatepassAPI.getActive();
      setPasses(response.data.gatePasses || []);
    } catch (error) {
      toast.error('Failed to fetch gate passes');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifySecurity = async (orderId) => {
    try {
      await gatepassAPI.notifySecurity(orderId);
      toast.success('Security has been notified!');
      fetchPasses();
    } catch (error) {
      toast.error('Failed to notify security');
    }
  };

  const copyPassCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Pass code copied!');
  };

  const getTimeRemaining = (validUntil) => {
    const diff = new Date(validUntil) - new Date();
    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${mins}m remaining`;
    return `${mins}m remaining`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <FiCheckCircle className="status-icon active" />;
      case 'used': return <FiCheckCircle className="status-icon used" />;
      case 'expired': return <FiAlertCircle className="status-icon expired" />;
      default: return <FiClock className="status-icon pending" />;
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = { swiggy: '🍔', zomato: '🍕', bigbasket: '🛒', blinkit: '⚡' };
    return icons[platform] || '📦';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="gatepasses-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1><FiKey /> Gate Passes</h1>
            <p>Manage entry passes for your deliveries</p>
          </div>
        </div>

        {passes.length === 0 ? (
          <div className="empty-state card">
            <FiKey className="empty-icon" />
            <h3>No active gate passes</h3>
            <p>Gate passes are automatically generated when you have orders</p>
            <Link to="/orders" className="btn btn-primary">
              View Orders
            </Link>
          </div>
        ) : (
          <div className="passes-grid">
            {passes.map(pass => (
              <div key={pass._id} className={`pass-card card ${pass.status}`}>
                <div className="pass-header">
                  {getStatusIcon(pass.status)}
                  <span className={`pass-status ${pass.status}`}>
                    {pass.status}
                  </span>
                  <span className="pass-gate-system">{pass.gateSystem}</span>
                </div>

                <div className="pass-code-section">
                  <span className="pass-label">Entry Code</span>
                  <div className="pass-code-display">
                    <span className="pass-code">{pass.passCode}</span>
                    <button
                      className="copy-btn"
                      onClick={() => copyPassCode(pass.passCode)}
                      title="Copy code"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>

                {pass.order && (
                  <div className="pass-order-info">
                    <span className="platform-icon">{getPlatformIcon(pass.order.platform)}</span>
                    <div>
                      <span className="platform-name">{pass.order.platform}</span>
                      <span className="order-id">#{pass.order.platformOrderId}</span>
                    </div>
                  </div>
                )}

                {pass.visitorDetails?.name && (
                  <div className="visitor-info">
                    <strong>Visitor:</strong> {pass.visitorDetails.name}
                    {pass.visitorDetails.vehicleNumber && (
                      <span> • {pass.visitorDetails.vehicleNumber}</span>
                    )}
                  </div>
                )}

                {pass.location && (
                  <div className="pass-location">
                    <strong>{pass.location.label}</strong>
                    <span>{pass.location.address?.full}</span>
                  </div>
                )}

                <div className="pass-validity">
                  <FiClock />
                  <span>{getTimeRemaining(pass.validUntil)}</span>
                </div>

                {pass.status === 'pending' || pass.status === 'active' ? (
                  <div className="pass-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleNotifySecurity(pass.order?._id)}
                      disabled={pass.securityNotified}
                    >
                      <FiBell />
                      {pass.securityNotified ? 'Security Notified' : 'Notify Security'}
                    </button>
                    <Link to={`/orders/${pass.order?._id}`} className="btn btn-outline">
                      View Order
                    </Link>
                  </div>
                ) : null}

                {pass.securityNotified && (
                  <div className="security-notified">
                    <FiCheckCircle /> Security was notified
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="info-card card">
          <h3>How Gate Passes Work</h3>
          <ul>
            <li>Gate passes are auto-generated when you register an order</li>
            <li>Share the code with your delivery rider if needed</li>
            <li>Click "Notify Security" when the rider is nearby</li>
            <li>Passes expire 3 hours after creation</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GatePasses;
